import type {ChooseGroup, ChooseOption, ChooseType, FeatureProps} from './types';

type RawOption = string | {key: string; label?: string; props?: FeatureProps | null; choose?: unknown};

type RawGroup = Omit<Partial<ChooseGroup>, 'type' | 'options'> & {
	key: string;
	type?: string;
	options?: RawOption[] | RawOption[][];
};

const TYPE_ALIASES: Record<string, ChooseType> = {
	children: 'subClass',
	musicalInstrument: 'instrument',
};

const TYPE_BY_KEY: Record<string, ChooseType> = {
	skill: 'skill',
	subClass: 'subClass',
	musicalInstrumentProficiency: 'instrument',
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === 'object' && !Array.isArray(value);

export function normalizeChoose(raw: unknown): ChooseGroup[] {
	if (!raw) return [];
	const entries = Array.isArray(raw) ? raw : [raw];
	return entries
		.flatMap((entry) => (Array.isArray(entry) ? entry : [entry]))
		.filter(isObject)
		.map((group) => normalizeGroup(group as RawGroup));
}

function normalizeGroup(raw: RawGroup): ChooseGroup {
	const {max} = raw;
	return {
		key: raw.key,
		type: resolveType(raw),
		label: raw.label || '',
		options: normalizeOptions(raw.options),
		max,
		multiple: !!raw.multiple || typeof max === 'string' || (typeof max === 'number' && max > 1),
		default: raw.default,
		change: raw.change,
		changeLimit: raw.changeLimit,
		params: raw.params || {},
		filters: raw.filters || {},
	};
}

function resolveType(raw: RawGroup): ChooseType {
	if (raw.key === 'expertise') return 'expertise';
	if (raw.type) return TYPE_ALIASES[raw.type] || (raw.type as ChooseType);
	return TYPE_BY_KEY[raw.key] || 'option';
}

function normalizeOptions(raw: RawGroup['options']): ChooseOption[] | undefined {
	if (!Array.isArray(raw) || !raw.length) return undefined;
	return (raw as RawOption[] | RawOption[][])
		.flat()
		.filter(Boolean)
		.map((option) =>
			typeof option === 'string'
				? {key: option}
				: {
						key: option.key,
						label: option.label,
						props: option.props || undefined,
						choose: normalizeChoose(option.choose),
				  },
		);
}
